import {
  Button,
  Card,
  DataStatus,
  Flag,
  IconButton,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Heading, HStack, Text, VStack} from 'platform/foundation';

import {indexBy, isEmpty} from 'ramda';

import {useGetSeriesListQuery} from '@dms/api/core';
import {useListRentalSettingsQuery, useRemoveRentalSettingsMutation} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {EMPTY_PLACEHOLDER, handleApiError, useBranches} from '@dms/shared';

import {getSafely} from 'shared/web';

import {PutDefinitionForm} from './components/PutDefinitionForm';
import {TenantSeriesCard} from './components/TenantSeriesCard';

export function CarRentalSeries() {
  const {
    data: rentalSettings,
    isLoading: isLoadingRentalSettings,
    isError: isErrorRentalSettings,
  } = useListRentalSettingsQuery({});
  const {
    data: series,
    isLoading: isLoadingSeries,
    isError: isErrorSeries,
  } = useGetSeriesListQuery({type: ['car-rental/rental-order']});

  const [deleteDefinition] = useRemoveRentalSettingsMutation();

  const handleDeleteDefinition = (rentalSettingsId: string) =>
    openDeleteDialog({
      text: i18n.t('entity.rental.labels.deleteDefinitionConfirmationText'),
      onConfirm: () =>
        deleteDefinition({rentalSettingsId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .catch(handleApiError),
    });

  const seriesById = indexBy((series) => series.id, series ?? []);
  const {getBranchById} = useBranches();

  const isLoading = isLoadingRentalSettings || isLoadingSeries;
  const isError = isErrorRentalSettings || isErrorSeries;

  const overrides = rentalSettings?.filter((settings) => settings.branchId !== null);

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('entity.rental.labels.carRentalSeries'),
        breadcrumbs: [
          {
            isCurrentPage: true,
            label: i18n.t('entity.rental.labels.rental'),
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.carRentalSeries('layout')}
    >
      <SettingsSection>
        <VStack spacing={4}>
          <TenantSeriesCard rentalSettings={rentalSettings ?? []} seriesById={seriesById} />

          <HStack justify="space-between" align="center">
            <Heading size={4}>{i18n.t('entity.rental.labels.branchSettings')}</Heading>

            <Button
              title={i18n.t('entity.rental.labels.addDefinition')}
              leftIcon="content/add_circle"
              onClick={() =>
                openDialog(<PutDefinitionForm />, {
                  title: i18n.t('entity.rental.labels.addDefinition'),
                })
              }
              variant="link"
            />
          </HStack>

          <DataStatus
            isEmpty={isEmpty(overrides)}
            spacing={10}
            action={{
              title: i18n.t('entity.rental.labels.addDefinition'),
              leftIcon: 'content/add_circle',
              variant: 'link',
              onClick: () =>
                openDialog(<PutDefinitionForm />, {
                  title: i18n.t('entity.rental.labels.addDefinition'),
                }),
            }}
          >
            {overrides?.map((setting) => (
              <Card variant="inlineGrey" key={setting.rentalSettingsId}>
                <HStack justify="space-between" align="center">
                  <HStack spacing={2} align="center">
                    <Text color="tertiary" size="small">
                      {setting.branchId
                        ? (getBranchById(setting.branchId)?.marketingName ?? EMPTY_PLACEHOLDER)
                        : EMPTY_PLACEHOLDER}
                      :
                    </Text>
                    <Flag
                      label={`${getSafely(seriesById, setting.orderSeriesId)?.name ?? setting.orderSeriesId}`}
                      colorScheme="teal"
                      isSubtle
                      size="small"
                    />
                  </HStack>

                  <HStack spacing={2} align="center">
                    <IconButton
                      icon="image/edit"
                      size="small"
                      onClick={() =>
                        openDialog(<PutDefinitionForm settingsId={setting.rentalSettingsId} />, {
                          title: i18n.t('entity.rental.labels.editDefinition'),
                        })
                      }
                    />
                    <IconButton
                      icon="action/delete"
                      severity="danger"
                      size="small"
                      onClick={() => handleDeleteDefinition(setting.rentalSettingsId)}
                    />
                  </HStack>
                </HStack>
              </Card>
            ))}
          </DataStatus>
        </VStack>
      </SettingsSection>
    </SettingsTemplate>
  );
}
